import React from 'react';
import type { LiveSummaryP1Shadow } from '../../api/liveSummaryApi';
import { toP1ShadowView } from './p1ShadowView';
import { geopoliticsForDisplay } from './geopoliticalPresentation';
import './p1ShadowEvidence.css';

type Props = { shadow?: LiveSummaryP1Shadow | null; macro?: unknown; compact?: boolean };

function localTimestamp(value: string): string {
  if (!value) return '시간 미확인';
  const when = new Date(value);
  if (!Number.isFinite(when.getTime())) return '시간 미확인';
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Seoul', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false,
  }).formatToParts(when);
  const get = (part: string) => parts.find((item) => item.type === part)?.value ?? '--';
  return `${get('month')}.${get('day')} ${get('hour')}:${get('minute')} KST`;
}

export default function P1ShadowEvidenceCard({ shadow, macro, compact = false }: Props) {
  const view = toP1ShadowView(shadow);
  // Last saved quote is an explicitly separate reference, NOT a realtime P1 signal.
  type SavedRow = { value?: number; day_change_rate_pct?: number; trade_date?: string; source_event_at?: string };
  type SavedContext = { status?: string; index_status?: string; stock_status?: string;
    indices?: Record<string, SavedRow>; stocks?: Record<string, SavedRow>;
    captured_at?: string; not_real_time?: boolean };
  const savedContext = (shadow as unknown as {
    realtime_observation?: { session_quote_context?: SavedContext }
  } | null | undefined)?.realtime_observation?.session_quote_context;
  const savedIndices = Object.entries(savedContext?.indices ?? {}).slice(0, 4);
  const savedStocks = Object.entries(savedContext?.stocks ?? {}).slice(0, 6);
  const formatSaved = (v?: number) => typeof v === 'number' && Number.isFinite(v)
    ? `${v > 0 ? '+' : ''}${v.toFixed(2)}%` : '변동률 미확인';
  const geoEvents = geopoliticsForDisplay(macro);
  return (
    <section className={`p1-shadow-card${compact ? ' is-compact' : ''}`} aria-label="P1 10분 검증 근거">
      <header className="p1-shadow-card__header">
        <div><small>P1 · 10-MIN EVIDENCE</small><strong>{view.title}</strong></div>
        <span className="p1-shadow-card__status">{view.available ? (view.stale ? '지난 기록' : '진단 기록') : '자료 미연결'}</span>
      </header>
      <p>{view.summary}</p>
      <dl className="p1-shadow-card__facts">
        <div><dt>지수 데이터</dt><dd>{view.quoteStatus === 'OBSERVED' ? '관측됨' : view.quoteStatus === 'NO_FRESH_QUOTES' ? '최신 시세 없음' : '확인 필요'}</dd></div>
        <div><dt>연결 종목</dt><dd>{view.stockStatus === 'NO_CONTEXT_TICKERS' ? '직접 연결 없음' : view.stockStatus === 'OBSERVED' ? '관측됨' : '검증 대기'}</dd></div>
        <div><dt>관측 이력</dt><dd>{view.observationCount === null ? '미확인' : `${view.observationCount}건`}</dd></div>
        <div><dt>수집 시각</dt><dd>{localTimestamp(view.snapshotAt)}</dd></div>
      </dl>
      {geoEvents.length > 0 && (
        <section className="p1-shadow-card__geo" aria-label="지정학적 사건과 시장 관측">
          <strong>국제정세 · 시장 영향 점검</strong>
          {geoEvents.map((item, i) => (
            <div key={`${item.headline}-${i}`} className="p1-shadow-card__geo-row">
              <p>{item.headline}</p>
              <small>{item.timeLabel ? `${item.timeLabel} · ` : ''}사건 관측 · 독립 확인 전</small>
              {item.assets.length > 0 ? (
                <div className="p1-shadow-card__geo-assets">
                  {item.assets.map((a, j) => (
                    <span key={`${a.name}-${j}`}>{a.name} <b>{a.returnPct >= 0 ? '+' : ''}{a.returnPct.toFixed(2)}%</b></span>
                  ))}
                  <small>각 자산의 전후 관측 가격 변화이며, 뉴스 영향이 입증된 것은 아닙니다.</small>
                </div>
              ) : (
                <small>관련 지수·선물의 사건 전후 가격 반응은 아직 검증되지 않았습니다.</small>
              )}
            </div>
          ))}
        </section>
      )}
      {savedContext?.status === 'SAVED_QUOTE_AVAILABLE' && savedContext.not_real_time === true && (
        <aside aria-label="마지막 저장 시세 참고" style={{ marginTop: 12, borderTop: '1px solid #edf1f5', paddingTop: 10 }}>
          <strong style={{ fontSize: 13 }}>최근 유효 시세 · 실시간 아님</strong>
          <p style={{ fontSize: 12, margin: '6px 0', color: '#52647b' }}>
            이전 체결의 전일 대비 등락입니다. 이 10분 구간의 가격 변화나 향후 방향 예측이 아닙니다.
          </p>
          {savedIndices.length > 0 && (
            <dl className="p1-shadow-card__facts">
              {savedIndices.map(([name, quote]) => (
                <div key={name}><dt>{name} · {quote.trade_date ?? '날짜 미확인'}</dt>
                  <dd>{typeof quote.value === 'number' ? quote.value.toLocaleString('ko-KR') : '가격 미확인'} · {formatSaved(quote.day_change_rate_pct)}</dd>
                  <small>체결 {localTimestamp(quote.source_event_at ?? '')}</small>
                </div>
              ))}
            </dl>
          )}
          {savedStocks.length > 0 && (
            <div className="p1-shadow-card__samples">
              {savedStocks.map(([ticker, quote]) => (
                <span key={ticker}>{ticker} · 전일 대비 <b>{formatSaved(quote.day_change_rate_pct)}</b>
                  {' · '}{quote.trade_date ?? '날짜 미확인'}</span>
              ))}
              <small>뉴스와 직접 연결된 종목 중 저장된 일부만 표시하며, 뉴스 영향이나 수급 주체를 뜻하지 않습니다.</small>
            </div>
          )}
          {savedIndices.length === 0 && savedStocks.length === 0 && (
            <small>유효한 최근 저장 시세가 없습니다.</small>
          )}
        </aside>
      )}
      {view.changes.length > 0 && (
        <div className="p1-shadow-card__samples" aria-label="시간 간격별 실측 지수 변화">
          {view.changes.map((s) => <span key={`${s.horizon}-${s.label}`}>
            {s.label} {s.horizon} <b>{s.returnPct > 0 ? '+' : ''}{s.returnPct.toFixed(2)}%</b>
          </span>)}
          <small>두 관측시각 간 변화입니다. 뉴스 원인이나 매수 신호는 아닙니다.</small>
        </div>
      )}
      <footer>10분 구간 종료: {localTimestamp(view.windowEnd)} · 검증 전 판단/상승확률 표시 안 함 · 매매 신호 아님</footer>
    </section>
  );
}
